import { format } from 'date-fns';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Area,
  AreaChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Bar,
  BarChart,
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Pie,
  PieChart,
  Cell,
} from 'recharts';
import { ResolutionBreakdownCard } from './ResolutionBreakdownCard';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/Domain/components/Shared/Card/Card';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
} from '@/Domain/components/Shared/Chart/chart';
import { ChartPlaceholder } from '@/Domain/components/Shared/ChartPlaceholder';

interface AnalyticsChartsSectionProps {
  outcomeTotals: Record<string, number>;
  outcomeLabels: readonly string[];
  isOutcomeLoading: boolean;
  conversationsByDay: Record<string, number>;
  messagesByDay: Record<string, number>;
  conversationsByWeekday: Record<string, number>;
  conversationsByHour: Record<string, number>;
  conversationsBySource: Record<string, number>;
  conversationsByLanguage: Record<string, number>;
  conversationsByMessages: Record<string, number>;
  durationBuckets: Record<string, number>;
  durationCutoff: number;
  durationOutliers: number;
  isLoading: boolean;
}

export const AnalyticsChartsSection = ({
  outcomeTotals,
  outcomeLabels,
  isOutcomeLoading,
  conversationsByDay,
  messagesByDay,
  conversationsByWeekday,
  conversationsByHour,
  conversationsBySource,
  conversationsByLanguage,
  conversationsByMessages,
  durationBuckets,
  durationCutoff,
  durationOutliers,
  isLoading,
}: AnalyticsChartsSectionProps) => {
  const { t } = useTranslation(['dashboard', 'common']);

  // Combined chart data for dual area chart
  const combinedChartData = useMemo(() => {
    const allDates = new Set([...Object.keys(conversationsByDay), ...Object.keys(messagesByDay)]);

    return Array.from(allDates)
      .sort()
      .map((date) => ({
        date,
        conversations: conversationsByDay[date] || 0,
        messages: messagesByDay[date] || 0,
      }));
  }, [conversationsByDay, messagesByDay]);

  const dualChartConfig = {
    conversations: {
      label: t('dashboard:conversations.conversations'),
      color: 'var(--chart-1)',
    },
    messages: {
      label: t('dashboard:conversations.messages'),
      color: 'var(--chart-2)',
    },
  } as const;

  // Conversations by weekday (Radar chart)
  const weekdayChartData = useMemo(() => {
    const keys = [
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday',
      'sunday',
    ] as const;
    // Backend uses Django ExtractWeekDay: 1=Sunday ... 7=Saturday
    // We want Monday..Sunday order, so map to [2,3,4,5,6,7,1]
    const apiWeekdayKeys = [2, 3, 4, 5, 6, 7, 1] as const;
    return apiWeekdayKeys.map((apiKey, i) => ({
      day: t(`common:weekdays_short.${keys[i]}`),
      value: Number((conversationsByWeekday as Record<string, number>)[String(apiKey)] ?? 0),
    }));
  }, [conversationsByWeekday, t]);

  const weekdayChartConfig = {
    value: {
      label: t('dashboard:conversations.number_of_conversations'),
      color: 'var(--chart-primary)',
    },
  } as const;

  // Conversations by hour (Bar chart)
  const hourChartData = useMemo(() => {
    // Convert UTC hours from backend to local timezone
    const timezoneOffsetHours = -new Date().getTimezoneOffset() / 60; // +2 for CET/CEST

    return Array.from({ length: 24 }, (_, localHour) => {
      // Calculate corresponding UTC hour
      const utcHour = (localHour - timezoneOffsetHours + 24) % 24;
      const count = Number((conversationsByHour as Record<string, number>)[String(utcHour)] ?? 0);

      return {
        hour: localHour,
        count,
      };
    });
  }, [conversationsByHour]);

  const hourChartConfig = {
    count: {
      label: t('dashboard:conversations.number_of_conversations'),
      color: 'var(--chart-primary)',
    },
  } as const;

  // Conversations by source (Pie chart)
  const sourcePieData = useMemo(() => {
    const MAX_SLICES = 9;
    const entries = Object.entries(conversationsBySource).map(([key, val]) => {
      const raw = t(`common:source.${key}`, key.replaceAll('_', ' '));
      const name =
        typeof raw === 'string' && raw.length > 0
          ? raw.charAt(0).toUpperCase() + raw.slice(1)
          : raw;
      return {
        name,
        value: Number(val ?? 0),
      };
    });
    // Sort desc by value
    const sorted = entries.sort((a, b) => b.value - a.value);
    const top = sorted.slice(0, MAX_SLICES);
    const rest = sorted.slice(MAX_SLICES);
    const othersTotal = rest.reduce(
      (sum, it) => sum + (Number.isFinite(it.value) ? it.value : 0),
      0,
    );
    const othersLabel = t('common:others', 'Others');
    const final = othersTotal > 0 ? [...top, { name: othersLabel, value: othersTotal }] : top;
    // Assign colors
    return final.map((item, idx) => ({ ...item, fill: `var(--chart-primary-${(idx % 10) + 1})` }));
  }, [conversationsBySource, t]);

  const sourceLegendValueMap = useMemo(
    () => Object.fromEntries(sourcePieData.map((d) => [d.name, d.value])),
    [sourcePieData],
  );

  const sourcePieConfig = useMemo(
    () => Object.fromEntries(sourcePieData.map((d) => [d.name, { label: d.name }] as const)),
    [sourcePieData],
  );

  const sourceLegendPayload = useMemo(
    () => sourcePieData.map((d) => ({ value: d.name, name: d.name, color: d.fill }) as any),
    [sourcePieData],
  );

  // Conversations by language (Pie chart)
  const languageDisplayNames = useMemo(
    () => new Intl.DisplayNames(['en'], { type: 'language' }),
    [],
  );
  const getLanguageName = useCallback(
    (code: string): string => {
      try {
        return languageDisplayNames.of(code) ?? code.toUpperCase();
      } catch {
        return code.toUpperCase();
      }
    },
    [languageDisplayNames],
  );

  const languagePieData = useMemo(() => {
    const MAX_SLICES = 9;
    const entries = Object.entries(conversationsByLanguage).map(([code, val]) => ({
      code,
      name: getLanguageName(code),
      value: Number(val ?? 0),
    }));
    const sorted = entries.sort((a, b) => b.value - a.value);
    const top = sorted.slice(0, MAX_SLICES);
    const rest = sorted.slice(MAX_SLICES);
    const othersTotal = rest.reduce(
      (sum, it) => sum + (Number.isFinite(it.value) ? it.value : 0),
      0,
    );
    const othersLabel = t('common:others', 'Others');
    const final =
      othersTotal > 0 ? [...top, { code: 'others', name: othersLabel, value: othersTotal }] : top;
    return final.map((item, idx) => ({ ...item, fill: `var(--chart-primary-${(idx % 12) + 1})` }));
  }, [conversationsByLanguage, t]);

  const languageLegendValueMap = useMemo(
    () => Object.fromEntries(languagePieData.map((d) => [d.name, d.value])),
    [languagePieData],
  );

  const languagePieConfig = useMemo(
    () => Object.fromEntries(languagePieData.map((d) => [d.name, { label: d.name }] as const)),
    [languagePieData],
  );

  const languageLegendPayload = useMemo(
    () => languagePieData.map((d) => ({ value: d.name, name: d.name, color: d.fill }) as any),
    [languagePieData],
  );

  // Distribution: conversations by AI messages count (Bar chart)
  const messagesDistData = useMemo(
    () =>
      Object.entries(conversationsByMessages)
        .map(([k, v]) => ({ messages: Number(k), count: Number(v ?? 0) }))
        .sort((a, b) => a.messages - b.messages),
    [conversationsByMessages],
  );
  const messagesChartConfig = {
    count: {
      label: t('dashboard:conversations.number_of_conversations'),
      color: 'var(--chart-primary)',
    },
  } as const;

  // Distribution: conversations by duration in minutes (Bar chart)
  const durationDistData = useMemo(() => {
    const arr = Object.entries(durationBuckets)
      .map(([k, v]) => ({ minute: Number(k), label: String(Number(k)), count: Number(v ?? 0) }))
      .sort((a, b) => a.minute - b.minute);
    if (durationCutoff > 0) {
      arr.push({
        minute: durationCutoff,
        label: `≥ ${durationCutoff}`,
        count: Number(durationOutliers || 0),
      });
    }
    return arr;
  }, [durationBuckets, durationCutoff, durationOutliers]);
  const durationChartConfig = {
    count: {
      label: t('dashboard:conversations.number_of_conversations'),
      color: 'var(--chart-primary)',
    },
  } as const;

  // Check if there's data for each chart type
  const hasCombinedData =
    combinedChartData &&
    combinedChartData.length > 0 &&
    combinedChartData.some((d) => d.conversations > 0 || d.messages > 0);
  const hasWeekdayData =
    weekdayChartData && weekdayChartData.length > 0 && weekdayChartData.some((d) => d.value > 0);
  const hasHourData =
    hourChartData && hourChartData.length > 0 && hourChartData.some((d) => d.count > 0);
  const hasSourceData =
    sourcePieData && sourcePieData.length > 0 && sourcePieData.some((d) => d.value > 0);
  const hasLanguageData =
    languagePieData && languagePieData.length > 0 && languagePieData.some((d) => d.value > 0);
  const hasMessagesDistData =
    messagesDistData && messagesDistData.length > 0 && messagesDistData.some((d) => d.count > 0);
  const hasDurationDistData =
    durationDistData && durationDistData.length > 0 && durationDistData.some((d) => d.count > 0);

  return (
    <>
      {/* Resolution Breakdown + Conversations & Messages */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <ResolutionBreakdownCard
          totals={outcomeTotals}
          labels={outcomeLabels}
          isLoading={isOutcomeLoading}
        />

        {/* Dual Area Chart - Conversations and Messages Together */}
        <Card className={`lg:col-span-2 pt-0 ${isLoading ? 'animate-pulse' : ''}`}>
          <CardHeader>
            <CardTitle>
              {t('dashboard:conversations.conversations_messages', 'Conversations & Messages')}
            </CardTitle>
          </CardHeader>
          <CardContent className="px-2 pt-4 sm:px-6 sm:pt-6">
            {!hasCombinedData ? (
              <ChartPlaceholder
                icon="mingcute:sad-line"
                titleTranslationKey="no_data_in_range.title"
                descriptionTranslationKey="no_data_in_range.description"
                spacing="small"
              />
            ) : (
              <ChartContainer
                config={dualChartConfig as any}
                className="aspect-auto h-[250px] w-full"
              >
                <AreaChart data={combinedChartData}>
                  <defs>
                    <linearGradient id="fillConversations" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="var(--chart-primary)" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="var(--chart-primary)" stopOpacity={0.1} />
                    </linearGradient>
                    <linearGradient id="fillMessages" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="var(--chart-neutral)" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="var(--chart-neutral)" stopOpacity={0.1} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid vertical={false} />
                  <XAxis
                    dataKey="date"
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    minTickGap={32}
                    tickFormatter={(value: any) => format(new Date(value), 'dd MMM')}
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    allowDecimals={false}
                    tickCount={5}
                    width={52}
                  />
                  <ChartTooltip
                    cursor={false}
                    content={
                      <ChartTooltipContent
                        labelFormatter={(value: any) =>
                          format(new Date(value as string), 'dd MMM yyyy')
                        }
                        indicator="dot"
                      />
                    }
                  />
                  <Area
                    dataKey="conversations"
                    type="monotone"
                    fill="url(#fillConversations)"
                    stroke="var(--chart-primary)"
                  />
                  <Area
                    dataKey="messages"
                    type="monotone"
                    fill="url(#fillMessages)"
                    stroke="var(--chart-neutral)"
                  />
                  <ChartLegend content={<ChartLegendContent />} />
                </AreaChart>
              </ChartContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Weekday and Hour charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Conversations per weekday (Radar) */}
        <Card className={isLoading ? 'animate-pulse' : ''}>
          <CardHeader>
            <CardTitle>{t('dashboard:conversations.conversations_per_week_day')}</CardTitle>
            <CardDescription>
              {t('dashboard:conversations.total_over_period', 'Across selected period')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!hasWeekdayData ? (
              <ChartPlaceholder
                icon="mingcute:sad-line"
                titleTranslationKey="no_data_in_range.title"
                descriptionTranslationKey="no_data_in_range.description"
                spacing="small"
              />
            ) : (
              <ChartContainer config={weekdayChartConfig} className="h-56 w-full">
                <RadarChart data={weekdayChartData} margin={{ left: 0, right: 0 }}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="day" />
                  <PolarRadiusAxis tick={false} />
                  <ChartTooltip
                    cursor={false}
                    content={
                      <ChartTooltipContent
                        indicator="dot"
                        className="w-auto"
                        labelFormatter={(_, payload) =>
                          ((payload?.[0] as any)?.payload?.day as string) || ''
                        }
                      />
                    }
                  />
                  <Radar
                    dataKey="value"
                    fill="var(--color-value)"
                    stroke="var(--color-value)"
                    fillOpacity={0.4}
                  />
                </RadarChart>
              </ChartContainer>
            )}
          </CardContent>
        </Card>

        {/* Conversations per hour (Bar) */}
        <Card className={isLoading ? 'animate-pulse' : ''}>
          <CardHeader>
            <CardTitle>{t('dashboard:conversations.conversations_per_hour')}</CardTitle>
            <CardDescription>
              {t('dashboard:conversations.total_over_period', 'Across selected period')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!hasHourData ? (
              <ChartPlaceholder
                icon="mingcute:sad-line"
                titleTranslationKey="no_data_in_range.title"
                descriptionTranslationKey="no_data_in_range.description"
                spacing="small"
              />
            ) : (
              <ChartContainer
                config={hourChartConfig}
                className={`h-56 w-full ${isLoading ? 'animate-pulse' : ''}`}
              >
                <BarChart accessibilityLayer data={hourChartData} margin={{ left: 0, right: 0 }}>
                  <CartesianGrid vertical={false} />
                  <XAxis
                    dataKey="hour"
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    tickFormatter={(value) =>
                      t('common:date', {
                        value: new Date(0, 0, 0, Number(value), 0, 0),
                        formatParams: { value: { hour: 'numeric' } },
                      })
                    }
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    allowDecimals={false}
                    width={52}
                  />
                  <ChartTooltip
                    cursor={false}
                    wrapperStyle={{ whiteSpace: 'normal' }}
                    labelFormatter={(_, payload) => {
                      const hour = Number((payload?.[0] as any)?.payload?.hour);
                      return t('common:date', {
                        value: new Date(0, 0, 0, Number.isFinite(hour) ? hour : 0, 0, 0),
                        formatParams: { value: { hour: 'numeric' } },
                      });
                    }}
                    content={<ChartTooltipContent indicator="dot" className="w-auto" />}
                  />
                  <Bar dataKey="count" fill="var(--color-count)" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ChartContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Sources and Languages (Pie charts) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Conversations by source (Pie) */}
        <Card className={isLoading ? 'animate-pulse' : ''}>
          <CardHeader>
            <CardTitle>
              {t('dashboard:messages.sources_of_conversations', 'Sources of conversations')}
            </CardTitle>
            <CardDescription>
              {t('dashboard:conversations.total_over_period', 'Across selected period')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!hasSourceData ? (
              <ChartPlaceholder
                icon="mingcute:sad-line"
                titleTranslationKey="no_data_in_range.title"
                descriptionTranslationKey="no_data_in_range.description"
                spacing="small"
              />
            ) : (
              <div className="grid grid-cols-[2fr,1fr] gap-6 items-start">
                <ChartContainer
                  config={sourcePieConfig as any}
                  className="mx-auto h-[250px] w-[250px] min-w-0"
                >
                  <PieChart>
                    <Pie
                      data={sourcePieData}
                      dataKey="value"
                      nameKey="name"
                      stroke="none"
                      innerRadius={60}
                    >
                      {sourcePieData.map((entry, idx) => (
                        <Cell key={`cell-source-${idx}`} fill={entry.fill as string} />
                      ))}
                    </Pie>
                    <ChartTooltip
                      cursor={false}
                      wrapperStyle={{ whiteSpace: 'normal' }}
                      content={<ChartTooltipContent hideLabel nameKey="name" className="w-auto" />}
                    />
                  </PieChart>
                </ChartContainer>
                <div className="pl-2">
                  <ChartContainer config={sourcePieConfig as any} fluidHeight className="h-[250px]">
                    <ChartLegendContent
                      nameKey="name"
                      valueMap={sourceLegendValueMap}
                      payload={sourceLegendPayload as any}
                      className="flex-col items-start justify-start gap-2"
                    />
                  </ChartContainer>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Conversations by language (Pie) */}
        <Card className={isLoading ? 'animate-pulse' : ''}>
          <CardHeader>
            <CardTitle>{t('dashboard:messages.languages', 'Languages')}</CardTitle>
            <CardDescription>
              {t('dashboard:conversations.total_over_period', 'Across selected period')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!hasLanguageData ? (
              <ChartPlaceholder
                icon="mingcute:sad-line"
                titleTranslationKey="no_data_in_range.title"
                descriptionTranslationKey="no_data_in_range.description"
                spacing="small"
              />
            ) : (
              <div className="grid grid-cols-[2fr,1fr] gap-6 items-start">
                <ChartContainer
                  config={languagePieConfig as any}
                  className="mx-auto h-[250px] w-[250px] min-w-0"
                >
                  <PieChart>
                    <Pie
                      data={languagePieData}
                      dataKey="value"
                      nameKey="name"
                      stroke="none"
                      innerRadius={60}
                    >
                      {languagePieData.map((entry, idx) => (
                        <Cell key={`cell-lang-${idx}`} fill={entry.fill as string} />
                      ))}
                    </Pie>
                    <ChartTooltip
                      cursor={false}
                      wrapperStyle={{ whiteSpace: 'normal' }}
                      content={<ChartTooltipContent hideLabel nameKey="name" className="w-auto" />}
                    />
                  </PieChart>
                </ChartContainer>
                <div className="pl-2">
                  <ChartContainer
                    config={languagePieConfig as any}
                    fluidHeight
                    className="h-[250px]"
                  >
                    <ChartLegendContent
                      nameKey="name"
                      valueMap={languageLegendValueMap}
                      payload={languageLegendPayload as any}
                      className="flex-col items-start justify-start gap-2"
                    />
                  </ChartContainer>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Messages and Duration distributions (Bar charts) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Distribution of AI messages per conversation */}
        <Card className={isLoading ? 'animate-pulse' : ''}>
          <CardHeader>
            <CardTitle>
              {t('dashboard:conversations.by_messages_distribution', 'Distribution of AI messages')}
            </CardTitle>
            <CardDescription>
              {t('dashboard:conversations.total_over_period', 'Across selected period')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!hasMessagesDistData ? (
              <ChartPlaceholder
                icon="mingcute:sad-line"
                titleTranslationKey="no_data_in_range.title"
                descriptionTranslationKey="no_data_in_range.description"
                spacing="small"
              />
            ) : (
              <ChartContainer
                config={messagesChartConfig}
                className={`h-56 w-full ${isLoading ? 'animate-pulse' : ''}`}
              >
                <BarChart accessibilityLayer data={messagesDistData} margin={{ left: 0, right: 0 }}>
                  <CartesianGrid vertical={false} />
                  <XAxis dataKey="messages" tickLine={false} axisLine={false} tickMargin={8} />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    allowDecimals={false}
                    width={52}
                  />
                  <ChartTooltip
                    cursor={false}
                    wrapperStyle={{ whiteSpace: 'normal' }}
                    labelFormatter={(_, payload) => {
                      const msgs = Number((payload?.[0] as any)?.payload?.messages);
                      return `${Number.isFinite(msgs) ? msgs : 0} ${t('common:messages', 'messages')}`;
                    }}
                    content={<ChartTooltipContent indicator="dot" className="w-auto" />}
                  />
                  <Bar dataKey="count" fill="var(--color-count)" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ChartContainer>
            )}
          </CardContent>
        </Card>

        {/* Distribution of conversations by duration (minutes) */}
        <Card className={isLoading ? 'animate-pulse' : ''}>
          <CardHeader>
            <CardTitle>
              {t(
                'dashboard:conversations.by_duration_distribution',
                'Distribution by duration (minutes)',
              )}
            </CardTitle>
            <CardDescription>
              {t('dashboard:conversations.total_over_period', 'Across selected period')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!hasDurationDistData ? (
              <ChartPlaceholder
                icon="mingcute:sad-line"
                titleTranslationKey="no_data_in_range.title"
                descriptionTranslationKey="no_data_in_range.description"
                spacing="small"
              />
            ) : (
              <ChartContainer
                config={durationChartConfig}
                className={`h-56 w-full ${isLoading ? 'animate-pulse' : ''}`}
              >
                <BarChart accessibilityLayer data={durationDistData} margin={{ left: 0, right: 0 }}>
                  <CartesianGrid vertical={false} />
                  <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={8} />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    allowDecimals={false}
                    width={52}
                  />
                  <ChartTooltip
                    cursor={false}
                    wrapperStyle={{ whiteSpace: 'normal' }}
                    labelFormatter={(_, payload) => {
                      const label = (payload?.[0] as any)?.payload?.label;
                      return `${label || ''} ${t('common:minute_single', 'minute')}`;
                    }}
                    content={<ChartTooltipContent indicator="dot" className="w-auto" />}
                  />
                  <Bar dataKey="count" fill="var(--color-count)" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ChartContainer>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
};
